/**
 * ContentUpdater - Handles modal content updates with crossfade animation
 *
 * @module ContentUpdater
 */

'use strict';

/**
 * Content Updater Class
 */
export class ContentUpdater {
    /**
     * Create content updater
     * @param {Object} config - Configuration
     */
    constructor(config) {
        this.config = config;
        this.transitionTimeout = null;
        this.isTransitioning = false;
    }

    /**
     * Update media content with crossfade animation
     * @param {string} modalId - Modal ID
     * @param {MediaData} mediaData - New media data
     * @param {string} mediaHTML - HTML for new media
     * @param {string} title - New title
     * @param {string} description - New description
     */
    async updateContent(modalId, mediaData, mediaHTML, title, description) {
        if (this.isTransitioning) {
            if (this.config.debugMode) {
                console.log('ContentUpdater: Transition in progress, skipping...');
            }
            return false;
        }

        this.isTransitioning = true;

        const modal = document.getElementById(modalId);
        if (!modal) {
            this.isTransitioning = false;
            return false;
        }

        await this.updateMediaWrapper(modal, mediaData, mediaHTML);
        this.updateTitle(modal, title);
        this.updateDescription(modal, description);

        return true;
    }

    /**
     * Update media wrapper with crossfade
     * @param {HTMLElement} modal - Modal element
     * @param {MediaData} mediaData - Media data
     * @param {string} mediaHTML - Media HTML
     * @private
     */
    async updateMediaWrapper(modal, mediaData, mediaHTML) {
        const wrapper = modal.querySelector('.bm-media-wrapper');
        if (!wrapper) {
            this.isTransitioning = false;
            return;
        }

        wrapper.style.aspectRatio = '';

        if (mediaData.type === 'image' && mediaData.width && mediaData.height) {
            wrapper.style.aspectRatio = `${mediaData.width} / ${mediaData.height}`;
        }

        if (mediaData.type === 'video' || mediaData.type === 'iframe') {
            wrapper.style.aspectRatio = '16 / 9';
        }

        const newContent = document.createElement('div');
        newContent.className = 'bm-media-content-new';
        newContent.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; opacity: 0; transition: opacity 0.3s ease;';
        newContent.innerHTML = mediaHTML;

        wrapper.style.position = 'relative';
        wrapper.appendChild(newContent);

        const newImage = newContent.querySelector('img');

        await this.startCrossfade(wrapper, newContent, newImage);
    }

    /**
     * Start crossfade animation
     * @param {HTMLElement} wrapper - Content wrapper
     * @param {HTMLElement} newContent - New content element
     * @param {HTMLImageElement|null} newImage - New image if present
     * @private
     */
    startCrossfade(wrapper, newContent, newImage) {
        return new Promise((resolve) => {
            const startTransition = () => {
                const oldContent = wrapper.querySelector('.bm-media-content-old, .bm-media-image, .bm-media-video, .bm-media-iframe');
                if (oldContent && oldContent !== newContent) {
                    oldContent.style.transition = 'opacity 0.3s ease';
                    oldContent.style.opacity = '0';
                }

                requestAnimationFrame(() => {
                    newContent.style.opacity = '1';
                });

                this.transitionTimeout = setTimeout(() => {
                    this.cleanupOldContent(wrapper, newContent);

                    this.isTransitioning = false;

                    if (this.config.debugMode) {
                        console.log('ContentUpdater: Crossfade complete');
                    }

                    resolve(true);
                }, 300);
            };

            if (newImage) {
                const makeLoaded = () => newImage.classList.add('bm-media-image--loaded');

                if (newImage.complete) {
                    makeLoaded();
                    startTransition();
                } else {
                    newImage.onload = () => {
                        makeLoaded();
                        startTransition();
                    };
                    newImage.onerror = () => {
                        makeLoaded();
                        startTransition();
                    };
                }

                setTimeout(() => {
                    makeLoaded();
                }, 200);
            } else {
                startTransition();
            }

            if (this.config.debugMode) {
                console.log('ContentUpdater: Starting crossfade');
            }
        });
    }

    /**
     * Cleanup old content after transition
     * @param {HTMLElement} wrapper - Content wrapper
     * @param {HTMLElement} newContent - New content element
     * @private
     */
    cleanupOldContent(wrapper, newContent) {
        Array.from(wrapper.children).forEach(child => {
            if (child !== newContent) {
                child.remove();
            }
        });

        while (newContent.firstChild) {
            wrapper.insertBefore(newContent.firstChild, newContent);
        }

        newContent.remove();

        wrapper.style.position = '';
    }

    /**
     * Update modal title
     * @param {HTMLElement} modal - Modal element
     * @param {string} title - Title text
     * @private
     */
    updateTitle(modal, title) {
        const titleEl = modal.querySelector('.bm-media-title');
        if (!titleEl) return;

        titleEl.textContent = title || '';
        titleEl.style.display = title ? 'block' : 'none';
    }

    /**
     * Update modal description
     * @param {HTMLElement} modal - Modal element
     * @param {string} description - Description text
     * @private
     */
    updateDescription(modal, description) {
        const descEl = modal.querySelector('.bm-media-description');
        if (!descEl) return;

        descEl.textContent = description || '';
        descEl.style.display = description ? 'block' : 'none';
    }

    /**
     * Clear timeout
     */
    clearTimeout() {
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    }

    /**
     * Check if transitioning
     * @returns {boolean} Is transitioning
     */
    isTransitioningNow() {
        return this.isTransitioning;
    }

    /**
     * Reset transition state
     */
    resetTransition() {
        this.isTransitioning = false;
    }
}

export default ContentUpdater;
